import ApiExecutor from './ApiExecutor';
import ApiContentType from './ApiContentType';
import store from '@/store';

/**
 * Merengueへのアクセスを担うクラス。
 *
 */
export default class Merengue extends ApiExecutor {
  constructor(siteId) {
    const api = process.env.API.MERENGUE;
    super(api);
  }

  /**
   * お気に入りスポット情報を取得します。
   * @param siteId サイトID
   * @param lang 言語コード
   * @param params パラメータ
   * @return {*}
   */
  getMySpots(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/my/spot`, params);
  }

  /**
   * お気に入りスポット情報を追加します。
   * @param siteId サイトID
   * @param lang 言語コード
   * @param body
   * @return {*}
   */
  addMySpot(siteId, lang, body) {
    return super.put(`/${siteId}/${lang}/my/spot?request_type=insert`, null, body, ApiContentType.JSON);
  }
  /**
   * お気に入りスポット情報を削除します。
   * @param siteId サイトID
   * @param lang 言語コード
   * @param code スポットコード
   * @param type スポットの種別
   * @return {*}
   */
  deleteMySpot(siteId, lang, code, type) {
    return super.delete(`/${siteId}/${lang}/my/spot/${code}`, {type: type}, null);
  }
  /**
   * ルートを取得します。
   */
  getRoute(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/route`, params);
  }

  /**
   * スポットを取得します
   * @param {*} siteId サイトID
   * @param {*} lang 言語
   * @param {*} params 検索パラメータ
   * @param {*} usesMochaResponseType レスポンスタイプがmocha形式かどうか
   * @returns スポット検索結果
   */
  getSpot(siteId, lang, params, usesMochaResponseType) {
    // レスポンス形式調整暫定対応
    params.mocha = usesMochaResponseType;
    return super.get(`/${siteId}/${lang}/spot/list`, params);
  }

  /**
   * 検索パラメータをAPIリクエストパラメータへ変換します
   * @param {*} params 検索パラメータ(spots/searchParams)
   */
  convertSearchParamInfoRequestParam(params) {
    const customize = store.state.system.customize;
    const requestParam = Object.assign({}, params);
    // nassパラメータ用
    let nassParam = {};
    // カテゴリパラメータ作成
    if (requestParam.category && requestParam.category.type) {
      // カテゴリのパラメータ作成
      if (requestParam.category.type === 'mocha') {
        // mocha
        if (requestParam.category?.code) {
          requestParam.category = requestParam.category.code;
        }
        // カテゴリが指定されている場合はtypeを上書き
        requestParam.type = 'mocha';
      } else if (requestParam.category.type === 'nass') {
        // nass
        // カテゴリのデータはすでにURLパラメータ形式になっているため、1度オブジェクトに変換
        nassParam = Object.fromEntries(new URLSearchParams(requestParam.category.condition));
        requestParam.category = '';
        // カテゴリが指定されている場合はtypeを上書き
        requestParam.type = 'nass';
      } else if  (requestParam.category.type === 'mocha.nass') {
        // nass
        // カテゴリのデータはすでにURLパラメータ形式になっているため、1度オブジェクトに変換
        nassParam = Object.fromEntries(new URLSearchParams(requestParam.category.nass_condition));
        // mocha
        requestParam.category = requestParam.category.mocha_code;
        requestParam.type = 'mocha.nass';
      }
    } else {
      // カテゴリ指定がない場合はカスタマイズで指定されているカテゴリを設定
      requestParam.category = customize.poiCategories ? customize.poiCategories : undefined;
      // カテゴリ指定がない場合はカスタマイズで指定されているデータセットを設定
      requestParam.type = customize.poiDataSet;
    }

    // アドレス指定がない場合は全体でカスタマイズで指定されている地域を設定
    if (!requestParam.address) {
      requestParam.address = customize.targetArea ? customize.targetArea : undefined;
    }
    // アドレス指定がある、かつ、全体の地域設定ではない場合はnassConditionに追加
    if (requestParam.address && requestParam.address !== customize.targetArea) {
      nassParam.address = requestParam.address;
    }

    // nassのURLパラメータ作成
    if (requestParam.type.includes('nass')) {
      if (requestParam.word) {
        // wordはスペースで区切って指定可能
        // eslint-disable-next-line no-irregular-whitespace
        nassParam.word = nassParam.word ? `${nassParam.word}　${requestParam.word}` : requestParam.word;
      }
      // nassConditionに変換
      requestParam.nassCondition = new URLSearchParams(nassParam).toString();
    }
    return requestParam;
  }

  /**
   * オートコンプリート用のスポット(新)
   * 適切なAPIを使用しています。
   */
  getAutocompleteSpotsWord(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/spot/autocomplete`, params);
  }

  /**
   * 緯度経度(Coord)から作成したスポット情報を取得します。
   */
  getSpotByCoord(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/spot/coord`, params);
  }

  /**
   * スポットコードからスポット画像を取得します。
   * */
  getSpotImageByCode(siteId, lang, code) {
    return super.get(`/${siteId}/${lang}/spot/${code}/images`);
  }

  /**
   * スポットコードからスポット情報を取得します。
   * */
  getSpotIntroductionByCode(siteId, lang, code, mochaLang) {
    return super.get(`/${siteId}/multilingual/spot/${code}/introduction`, {lang: mochaLang});
  }

  /**
   * 住所を取得します。
   */
  getAddress(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/address/`, params);
  }

  /**
   * 住所リストを取得します。
   */
  getAddressList(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/address/list`, params);
  }

  /**
   * POI 種別推定情報を取得します。
   * @param siteId
   * @param lang
   * @param params
   * @return {*}
   */
  getPoiTypeEstimate(siteId, lang, params) {
    // レスポンス形式調整暫定対応
    return super.get(`/${siteId}/${lang}/poi/type/estimate`, params);
  }

  /**
   * staticmap用のルート形状線を取得します。
   */
  scheduleStaticMapRouteShape(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/route/shape`, params);
  }

  /**
   * 移動手段別ルート情報を更新します。
   */
  getScheduleRoutes(siteId, lang, body) {
    // リクエスト時には必ず congestion と sightseeing_train を付与する
    body.add = 'congestion.sightseeing_train';
    return super.post(`/${siteId}/${lang}/trip/plan/schedule/route/comparison`, null, body, ApiContentType.JSON);
  }

  /**
   * カテゴリ一覧を取得します。
   * パラメータ不要
   * MapPageで使用。
   */
  getCategoryTree(siteId, lang) {
    return super.get(`/${siteId}/${lang}/spot/category`);
  }

  /**
   * サイトカスタマイズ情報を取得します。
   * パラメータ不要
   */
  getCustomizeData(siteId, lang) {
    return super.get(`/${siteId}/${lang}/site/customize/get`);
  }
  /**
   * seq を取得
   */
  getSeq(siteId, lang) {
    return super.get(`/${siteId}/${lang}/account/get-seq`);
  }

  /**
   * ユーザー情報を取得します。
   */
  getUserInfo(siteId, lang, params, userId, idToken) {
    const header = {
      user_id: userId,
      id_token: idToken,
    };
    return super.get(`/${siteId}/${lang}/account/user/get`, params, header);
  }

  /**
   * ユーザー情報を登録します。
   */
  registerUserInfo(siteId, lang, body) {
    return super.post(`/${siteId}/${lang}/account/user/`, null, body, ApiContentType.JSON);
  }

  /**
   * ユーザー情報を更新します。
   */
  updateUserInfo(siteId, lang, body, userId, idToken) {
    const header = {
      user_id: userId,
      id_token: idToken,
    };
    return super.put(`/${siteId}/${lang}/account/user`, null, body, ApiContentType.JSON, header);
  }

  /**
   * ユーザー情報を削除します。
   */
  deleteUser(siteId, lang, userId, idToken) {
    const header = {
      user_id: userId,
      id_token: idToken,
    };
    const params = {
      user_id: userId,
    };
    return super.delete(`/${siteId}/${lang}/account/user`, params, header);
  }

  /**
   * メールアドレスからユーザーIDを生成します。
   */
  createUserId(siteId, lang, body) {
    return super.post(`/${siteId}/${lang}/account/user/create`, null, body, ApiContentType.JSON);
  }

  /**
   * 登録済みのユーザーIDを取得します。(POST)
   */
  getUserId(siteId, lang, body) {
    return super.post(`/${siteId}/${lang}/account/user/get`, null, body, ApiContentType.JSON);
  }

  /**
   * 静的地図情報を取得します。
   * @param {*} siteId サイトID
   * @param {*} lang 言語
   * @param {*} params 検索パラメータ
   * @param {*} body marker, path, circleを指定しマーカー、線、円を指定可能
   * @returns 静的地図のバイナリデータ
   */
  getStaticMap(siteId, lang, params, body) {
    return super.post(`/${siteId}/${lang}/staticmap`, params, body, ApiContentType.JSON);
  }

  /**
   * 静的地図画像を取得します。
   * @param siteId
   * @param {*} siteId サイトID
   * @param {*} lang 言語
   * @param {*} params 検索パラメータ
   * @param {*} body marker, path, circleを指定しマーカー、線、円を指定可能
   * @return {*}
   */
  getStaticMapImage(siteId, lang, params, body) {
    return super.post(`/${siteId}/${lang}/staticmap/png`, params, body, ApiContentType.JSON, {});
  }

  /**
   * 祝日リストを返却します。
   * @param siteId
   * @param params
   * @returns {*}
   */
  getHolidayList(siteId, params) {
    return super.get(`/${siteId}/date/holiday/list`, params);
  }

  /**
   * ガイドマップのスポットを検索します（code指定）
   * @param {*} siteId サイトID
   * @param {*} lang 言語
   * @param {*} params 検索パラメータ
   * @returns スポット一覧
   */
  getGuideMapSpot(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/guide-map/spot`, params);
  }

  /**
   * ガイドマップのスポットを検索します
   * @param {*} siteId サイトID
   * @param {*} lang 言語
   * @param {*} params 検索パラメータ
   * @returns スポット一覧
   */
  getGuideMapSpotList(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/guide-map/spot/list`, params);
  }



  /**
   * 駅・バス停一覧を取得します。
   * @param siteId
   * @param lang
   * @param params
   * @return {*}
   */
  getTransportNodeList(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/transport/node/list`, params);
  }

  /**
   * 駅・バス停をノードid指定で取得します。
   * @param siteId
   * @param lang
   * @param params
   * @return {*}
   */
  getTransportNode(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/transport/node`, params);
  }

  /**
   * 市区町村別でnassのスポット数を取得します
   * @param siteId
   * @param lang
   * @param params
   * @return {*}
   */
  getGuideMapSpotCountAddress(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/guide-map/spot/count/address`, params);
  }

  /**
   * パラメータで渡したURLの短縮URLを取得します
   * @param params
   * @return {*}
   */
  getShortUrl(params) {
    return super.get('/short-url', params);
  }

  /**
   * オートコンプリート
   * @param siteId
   * @param lang
   * @param params
   * @returns {*}
   */
  getAutocomplete(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/autocomplete`, params);
  }

  /**
   * ガイドマップの移動手段別ルート結果を取得します。
   * @param siteId
   * @param lang
   * @param params
   * @returns {*}
   */
  getRouteResult(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/guide-map/route`, params);
  }

  /**
   * ホテルをcode指定で取得します
   * @param siteId
   * @param lang
   * @param params
   * @returns {*}
   */
  getHotel(siteId, lang, params) {
    return super.get(`/${siteId}/${lang}/guide-map/hotel`, params);
  }
  /**
  * POI検索ログを送信します
  * @param siteId
  * @param lang
  * @param params
  * @returns {*}
  */
  sendPoiLog(siteId, lang, body) {
    return super.post(`/${siteId}/${lang}/spot/freeword/rank/log`, {}, body, ApiContentType.JSON);
  }
}
